import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { login, verifyOtp } from '../../services/auth';
import { User } from '../../types/auth';

// Get user from localStorage if it exists
const getUserFromLocalStorage = (): User | null => {
  const userString = localStorage.getItem('user');
  if (!userString) return null;
  
  try {
    return JSON.parse(userString);
  } catch (error) {
    console.error('Failed to parse user from localStorage', error);
    return null;
  }
};

// Initial state with user restored from localStorage
const initialState = {
  user: getUserFromLocalStorage(),
  token: localStorage.getItem('token') || null,
  isAuthenticated: !!localStorage.getItem('token'),
  loading: false,
  error: null as string | null,
  otpHash: null as string | null,
  mobileNo: null as string | null,
}; 

export const sendOTP = createAsyncThunk(
  'auth/sendOTP',
  async (mobileNo: string, { rejectWithValue }) => {
    try {
      const response = await login(mobileNo);
      return { otpHash: response.otpHash, mobileNo };
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to send OTP');
    }
  }
);

export const verifyOTPThunk = createAsyncThunk(
  'auth/verifyOTP',
  async ({ mobileNo, otp, otpHash }: { mobileNo: string; otp: string; otpHash: string }, { rejectWithValue }) => {
    try {
      const response = await verifyOtp(mobileNo, otp, otpHash);
      
      // No need to create userData manually, it's coming from the API now
      return { 
        token: response.token, 
        user: response.user 
      };
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Verification failed');
    }
  }
);

// Auth slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
      state.isAuthenticated = true;
      // Store user in localStorage
      localStorage.setItem('user', JSON.stringify(action.payload));
    },
    clearAuth: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    expireOTP: (state) => {
      state.otpHash = null;
      // Clear OTP hash from sessionStorage as well
      sessionStorage.removeItem('otpHash');
    }
  },
  extraReducers: (builder) => {
    // Handle sendOTP
    builder.addCase(sendOTP.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(sendOTP.fulfilled, (state, action) => {
      state.loading = false;
      state.otpHash = action.payload.otpHash;
      state.mobileNo = action.payload.mobileNo;
      
      // Store in sessionStorage as backup in case state gets cleared
      sessionStorage.setItem('otpHash', action.payload.otpHash);
      sessionStorage.setItem('mobileNo', action.payload.mobileNo);
    });
    builder.addCase(sendOTP.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
    
    // Handle verifyOTP
    builder.addCase(verifyOTPThunk.pending, (state) => {
      state.loading = true;
      state.error = null;
      
      // Restore from sessionStorage if missing in state
      if (!state.otpHash) {
        state.otpHash = sessionStorage.getItem('otpHash');
      }
      if (!state.mobileNo) {
        state.mobileNo = sessionStorage.getItem('mobileNo');
      }
    });
    builder.addCase(verifyOTPThunk.fulfilled, (state, action) => {
      state.loading = false;
      
      const { token, user } = action.payload;
      
      // Update state with validated data
      state.token = token;
      state.user = user;
      state.isAuthenticated = true;
      
      // Store properly validated data in localStorage
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      
      // Clear OTP related info after successful verification
      state.otpHash = null;
      state.mobileNo = null;
      
      // Clean up sessionStorage
      sessionStorage.removeItem('otpHash');
      sessionStorage.removeItem('mobileNo');
    });
    builder.addCase(verifyOTPThunk.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
  }
});

// Export actions and reducer
export const { setUser, clearAuth, setError, expireOTP } = authSlice.actions;
export default authSlice.reducer;